/**
 * When a person's own plan comes back, in their own local time and the words
 * the engines mock uses (mockups/engines-and-plans-2026-09-28.html): "14:20"
 * today, "Thu 14:20" on any other day. One formatter for Settings' plan rows
 * (PlanStanding) and the plan_limit card and its waiting line.
 */

/** "14:20": 24-hour, two digits, local time. */
export const planClock = (d: Date): string =>
  d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });

/** "Thu": the short weekday, local time. */
export const planWeekday = (d: Date): string =>
  d.toLocaleDateString(undefined, { weekday: "short" });

/**
 * "14:20" when the moment falls today, "Thu 14:20" otherwise. Null for a
 * missing or unreadable time: the caller says "for now", never a made-up clock.
 */
export function planWhen(
  iso: string | null | undefined,
  now: number = Date.now()
): string | null {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return null;
  const d = new Date(t);
  return d.toDateString() === new Date(now).toDateString()
    ? planClock(d)
    : `${planWeekday(d)} ${planClock(d)}`;
}

/** How long a task still reads as waiting past its resumeAt: the sweep runs every 60 s and the computer must wake. */
export const PLAN_RESUME_GRACE_MS = 3 * 60_000;

/**
 * Engines T5 — is a task still waiting for its plan? Until its resumeAt plus
 * the grace above: one older than that is absent, so a clear this client
 * missed expires by itself instead of holding the task "paused".
 */
export function isWaiting(
  resumeAt: string | null | undefined,
  now: number = Date.now()
): boolean {
  const t = resumeAt ? Date.parse(resumeAt) : NaN;
  return Number.isFinite(t) && t + PLAN_RESUME_GRACE_MS > now;
}

/**
 * Several accounts — is this resumeAt the safety net a switch to the next
 * account armed (tasks.resume_at a few minutes out, taken back the moment the
 * next account's turn is pushed)? Only when it is that exact instant, which the
 * server names on the switched turn (AGENT_END and its footer, planLimitNetAt).
 * Compared as instants: the server writes the ISO it armed, a Realtime row
 * spells the same moment "+00:00". Any other resumeAt is a real pause.
 */
export function isSwitchNet(
  resumeAt: string | null | undefined,
  switchNetAt: string | null | undefined
): boolean {
  const a = resumeAt ? Date.parse(resumeAt) : NaN;
  const b = switchNetAt ? Date.parse(switchNetAt) : NaN;
  return Number.isFinite(a) && a === b;
}

/**
 * The later of two net times, compared as instants (a footer and a live frame may
 * spell one moment differently): net times only move forward, so a sync whose
 * newest footer predates what this client already knows never rolls it back.
 */
export function laterSwitchNet(
  known: string | null | undefined,
  footer: string | null
): string | null {
  if (!known) return footer;
  if (!footer) return known;
  return Date.parse(footer) > Date.parse(known) ? footer : known;
}

/** The net time on the newest switched turn's footer in a thread, if any (a reload's source of it). */
export function newestSwitchNet(
  messages: ReadonlyArray<{ content: ReadonlyArray<{ type: string }> }>
): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    for (const b of messages[i].content) {
      const at = (b as { planLimitNetAt?: unknown }).planLimitNetAt;
      if (b.type === "turn_footer" && typeof at === "string") return at;
    }
  }
  return null;
}
